import { useMemo } from "react";
import { Link } from "react-router-dom";
import { type ColumnDef } from "@tanstack/react-table";
import {
  Download,
  UserPlus,
  ArrowRight,
  MoreVertical,
} from "lucide-react";
import AdminPortalShell from "@/components/admin/AdminPortalShell";
import AdminTable from "@/components/admin/AdminTable";
import { ADMIN_USERS, type AdminUser } from "@/components/admin/_mockData";

/**
 * /admin/users — list of all staging users. Backed by `AdminTable`
 * (TanStack Table) as of ROADS-478. Sorting + global filter + pagination
 * are inherited from the shared component; the column definitions below
 * own the cell rendering (avatars, badges, pills, action buttons).
 *
 * Wires to `GET /api/admin/users` in ROADS-476 deliverable A1 — the
 * `ADMIN_USERS` fixture is the only thing that changes there.
 */
export default function AdminUsersListPage() {
  const columns = useMemo<ColumnDef<AdminUser, unknown>[]>(
    () => [
      {
        id: "name",
        accessorKey: "name",
        header: "User",
        cell: ({ row }) => {
          const u = row.original;
          return (
            <div className="flex items-center gap-3">
              <div
                aria-hidden="true"
                className="flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold text-white"
                style={{ backgroundColor: u.avatarHex }}
              >
                {u.initials}
              </div>
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-slate-900">
                  {u.name}
                </div>
                <div className="truncate text-[11px] text-slate-500">
                  {u.email} · {abbreviateId(u.id)}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        id: "persona",
        accessorKey: "persona",
        header: "Persona · Lang",
        cell: ({ row }) => {
          const u = row.original;
          return (
            <div className="min-w-0">
              <div className="truncate text-xs font-medium text-slate-900">
                {personaLabel(u.persona)} · {u.language}
              </div>
              <div className="truncate text-[11px] text-slate-500">
                Home: {u.homeCountry} {u.homeCountryEmoji}
              </div>
            </div>
          );
        },
      },
      {
        id: "target",
        accessorFn: (u) => u.targetStates.join(","),
        header: "Target states",
        enableSorting: false,
        cell: ({ row }) => {
          const states = row.original.targetStates;
          return (
            <div className="flex flex-wrap items-center gap-1">
              {states.slice(0, 3).map((st) => (
                <span
                  key={st}
                  className="rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 font-mono text-[11px] font-semibold text-slate-700"
                >
                  {st}
                </span>
              ))}
              {states.length > 3 && (
                <span className="text-[11px] font-semibold text-slate-500">
                  +{states.length - 3}
                </span>
              )}
            </div>
          );
        },
      },
      {
        id: "status",
        accessorFn: (u) => u.trialDaysLeft,
        header: "Status",
        cell: ({ row }) => (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
            <span
              aria-hidden="true"
              className="h-1.5 w-1.5 rounded-full bg-emerald-500"
            />
            Trial · {row.original.trialDaysLeft}d left
          </span>
        ),
      },
      {
        id: "access",
        accessorFn: (u) => accessSummary(u),
        header: "Access",
        enableSorting: false,
        cell: ({ getValue }) => (
          <span className="text-[11px] font-medium text-slate-700">
            {String(getValue())}
          </span>
        ),
      },
      {
        id: "last_seen",
        accessorKey: "lastSeen",
        header: "Last seen",
        cell: ({ row }) => (
          <div>
            <div className="text-xs font-medium text-slate-900">
              {row.original.lastSeen}
            </div>
            <div className="text-[10px] text-slate-500">
              {row.original.lastSeenDevice}
            </div>
          </div>
        ),
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => {
          const u = row.original;
          return (
            <div className="flex items-center justify-end gap-1">
              <Link
                to={`/admin/users/${u.id}`}
                className="flex items-center gap-1 rounded-lg bg-indigo-50 px-2.5 py-1.5 text-[11px] font-semibold text-indigo-700 no-underline hover:bg-indigo-100"
                data-testid={`admin-user-row-open-${u.id}`}
              >
                Open
                <ArrowRight aria-hidden="true" className="h-2.5 w-2.5" />
              </Link>
              <button
                type="button"
                aria-label="More actions"
                className="flex h-7 w-7 items-center justify-center rounded-lg hover:bg-slate-100"
              >
                <MoreVertical
                  aria-hidden="true"
                  className="h-3.5 w-3.5 text-slate-500"
                />
              </button>
            </div>
          );
        },
      },
    ],
    [],
  );

  return (
    <AdminPortalShell
      title="Users"
      subtitle="4 total · 2 new this week"
      testId="admin-users-list-page"
      action={
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            <Download aria-hidden="true" className="h-3.5 w-3.5" />
            Export CSV
          </button>
          <button
            type="button"
            className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
          >
            <UserPlus aria-hidden="true" className="h-3.5 w-3.5" />
            Invite admin
          </button>
        </div>
      }
    >
      <AdminTable
        data={ADMIN_USERS}
        columns={columns}
        enableGlobalFilter
        globalFilterPlaceholder="Search email, name, persona, WorkOS id…"
        pageSize={25}
        testId="admin-users-list"
      />
    </AdminPortalShell>
  );
}

function abbreviateId(id: string): string {
  if (id.length <= 18) return id;
  return id.slice(0, 12) + "…" + id.slice(-5);
}

function personaLabel(p: "immigrant" | "resident" | "cdl"): string {
  if (p === "immigrant") return "Immigrant";
  if (p === "cdl") return "CDL";
  return "Resident";
}

function accessSummary(u: AdminUser): string {
  const parts: string[] = [];
  if (u.targetStates.length === 1) {
    parts.push(`Class C · ${u.targetStates[0]}`);
  } else if (u.targetStates.length > 1) {
    parts.push(`Class C ×${u.targetStates.length}`);
  }
  if (u.cdlInterest) parts.push("CDL · TX");
  parts.push("comp ∞");
  return parts.join(" · ");
}
